import { motion } from "framer-motion";
import { useState } from "react";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export const ProductShowcase = ({ product, index = 0, total = 1 }) => {
    const { addItem } = useCart();
    const [size, setSize] = useState(null);

    const handleAdd = () => {
        if (!size) {
            toast.error("Pick a size first");
            return;
        }
        addItem(product, size);
        toast.success(`${product.name} — ${size} added to cart`);
    };

    return (
        <section id={index === 0 ? "drop" : undefined} data-testid={`product-showcase-${product.id}`} className="relative mx-auto max-w-7xl px-6 py-28 md:py-40">
            <motion.div {...fadeUp} className="mb-16 flex items-end justify-between border-b border-vault-border pb-6">
                <div>
                    <p className="font-mono text-xs tracking-[0.3em] text-vault-red">DROP 0{index + 1}</p>
                    <h2 className="mt-3 font-display text-5xl uppercase leading-none text-stone-900 md:text-7xl">
                        {product.name}
                    </h2>
                </div>
                <p className="hidden font-mono text-xs tracking-[0.2em] text-stone-400 md:block">
                    0{index + 1} / 0{total}
                </p>
            </motion.div>

            <div className="grid gap-12 lg:grid-cols-2">
                <motion.div
                    {...fadeUp}
                    className={`lg:sticky lg:top-28 lg:self-start ${index % 2 === 1 ? "lg:order-2" : ""}`}
                >
                    <div className="clip-frame relative border border-vault-border bg-white p-8">
                        <img
                            data-testid={`product-image-${product.id}`}
                            src={product.image}
                            alt={product.name}
                            className="w-full object-contain"
                        />
                    </div>
                    <p className="mt-4 font-mono text-[10px] tracking-[0.25em] text-stone-400">
                        PHOTOGRAPHED AS PAINTED — NO RETOUCH
                    </p>
                </motion.div>

                <motion.div
                    {...fadeUp}
                    transition={{ ...fadeUp.transition, delay: 0.15 }}
                    className={index % 2 === 1 ? "lg:order-1" : ""}
                >
                    <p data-testid={`product-edition-${product.id}`} className="font-mono text-xs tracking-[0.3em] text-vault-red">
                        {product.edition}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-4">
                        <p data-testid={`product-price-${product.id}`} className="font-mono text-2xl text-stone-900">
                            <span className="mr-3 text-lg text-stone-500 line-through">
                                ₹{product.original_price.toLocaleString("en-IN")}
                            </span>
                            ₹{product.price.toLocaleString("en-IN")}{" "}
                            <span className="text-xs text-stone-500">INR</span>
                        </p>
                        <span
                            data-testid={`sale-badge-${product.id}`}
                            className="bg-vault-red px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] text-stone-900"
                        >
                            {product.sale}
                        </span>
                    </div>
                    <p className="mt-6 max-w-lg text-sm leading-relaxed text-stone-600">{product.description}</p>

                    <div className="mt-10 grid grid-cols-2 gap-px border border-vault-border bg-vault-border">
                        {product.details.map((d) => (
                            <div key={d.label} className="bg-vault-black p-5">
                                <p className="font-mono text-[10px] tracking-[0.25em] text-stone-500">{d.label}</p>
                                <p className="mt-2 text-sm text-stone-900">{d.value}</p>
                            </div>
                        ))}
                    </div>

                    {product.custom_number && (
                        <div
                            data-testid={`custom-number-note-${product.id}`}
                            className="mt-8 border border-vault-red/40 bg-vault-surface/50 p-5"
                        >
                            <p className="font-mono text-xs tracking-[0.25em] text-vault-red">MAKE IT YOURS</p>
                            <p className="mt-2 text-sm leading-relaxed text-stone-600">
                                Pick any number from 1 to 10 and we hand-paint it on your pair. You choose the number
                                at checkout.
                            </p>
                        </div>
                    )}

                    <div className="mt-10">
                        <p className="mb-4 font-mono text-xs tracking-[0.3em] text-stone-500">SELECT SIZE</p>
                        <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
                            {product.sizes.map((s) => (
                                <button
                                    key={s}
                                    data-testid={`size-option-${product.id}-${s.replace(" ", "-").toLowerCase()}`}
                                    onClick={() => setSize(s)}
                                    className={`border px-2 py-3 font-mono text-xs transition-colors ${
                                        size === s
                                            ? "border-vault-red bg-vault-red text-stone-900"
                                            : "border-vault-border text-stone-600 hover:border-zinc-400 hover:text-stone-900"
                                    }`}
                                >
                                    {s.replace("US ", "")}
                                </button>
                            ))}
                        </div>
                    </div>

                    <button
                        data-testid={`add-to-cart-button-${product.id}`}
                        onClick={handleAdd}
                        className="btn-sweep mt-10 flex w-full items-center justify-center gap-3 border border-vault-red py-5 font-mono text-sm tracking-[0.25em] text-stone-900"
                    >
                        <ShoppingBag size={16} /> ADD TO CART — ₹{product.price.toLocaleString("en-IN")}
                    </button>
                    <p className="mt-4 font-mono text-[10px] tracking-[0.2em] text-stone-400">
                        MADE TO ORDER — SHIPS IN 5–7 DAYS — UPI QR OR CASH ON DELIVERY
                    </p>
                </motion.div>
            </div>
        </section>
    );
};
